import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import clsx from 'clsx';
import { useSelector, useDispatch } from 'react-redux';
import { get, isEmpty } from 'lodash';
import { ANOMALY_DESC, ENABLE_FACET_RAW_IMAGES } from 'layout/entitleUser/EntitleUser.constants';
import {
  EVBox,
  EVTypography,
  EVButton,
  EVFormControl,
  EVSelect,
  EVMenuItem,
  EVSwitch,
  EVPaperClipIcon,
  EVIconButton,
  EVNoteIcon,
  EVExpand,
  EVCircularProgress,
  EVRefreshOutlinedIcon,
} from '@eagleview/ev-comp-library';

import PropTypes from 'prop-types';
import ErrorBoundary from 'components/ErrorBoundary';
import {
  ANOMALY_DECISIONS,
  CONFIRMABLE_DECISIONS,
  POLLING_ANOMALY_SNAPSHOTS,
  LOADED_ANOMALY_SNAPSHOTS,
} from '../Adjuster.constants';
import useStyles from './AnomalyDecisionCard.styles';
import * as adjusterAction from '../Adjuster.actions';
import * as selectors from './AnomalyDecisions.selectors';

const AnomalyDecisionCard = ({
  t,
  anomalyTypeOptions,
  onTypeChange,
  showAnomalyDescription,
  isAnomalyDescriptionPresent,
  onOpenAnomalyDescription,
  UNSET_ANOMALY_ID,
  GET_ANOMALY_NOTES,
  isEntitled,
  FEATURE_ENTITLEMENTS,
  selectedAnomaly,
  getMappedAnomalyType,
  onMarkDamage,
  isFacetClickDisplay,
  FETCH_ANOMALY_METADATA,
  handleDeleteUserAnomalyOpen,
  confirmOrRemoveAnomalyDecision,
  onIncludeInReport,
  showIncludeInReport,
  includeInReportChecked,
  includeInReportDisabled,
  onExpandClick,
  highlight,
  onAnomalyCardHover,
}) => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const [snapshotData, setSnapshotData] = useState('');
  const [retryingSnapshot, setRetryingSnapshot] = useState(false);

  // Memoize derived values to prevent unnecessary re-renders
  const selectedAnomalyId = useMemo(
    () => get(selectedAnomaly, 'properties.anomalyId', UNSET_ANOMALY_ID),
    [get(selectedAnomaly, 'properties.anomalyId'), UNSET_ANOMALY_ID],
  );

  // Selectors
  const loading = useSelector((state) => state.adjusterReducer.loading);
  const entitlements = useSelector((state) => state.entitleUserReducer.entitlements);
  const orderCompleted = useSelector((state) => state.adjusterReducer.orderCompleted);
  const snapshotBytesFromStore = useSelector((state) => get(state.adjusterReducer.anomalySnapshots, selectedAnomalyId, ''));
  const error = useSelector(selectors.errorSelector);
  const showAnomalyNotesButton = useSelector((state) => get(state.entitleUserReducer.featureFlags, ANOMALY_DESC, false));
  const isFacetRawImagesEnabled = useSelector((state) => get(state.entitleUserReducer.featureFlags, ENABLE_FACET_RAW_IMAGES, false));
  const orderId = useSelector((state) => state.adjusterReducer.orderId);

  const clearAnomalySnapshot = (payload) => dispatch(adjusterAction.clearAnomalySnapshotsAction(payload));
  const generateAnomalySnapshots = (payload) => dispatch(adjusterAction.generateAnomalySnapshotsAction(payload));
  const fetchAnomalySnapshots = (payload) => dispatch(adjusterAction.fetchAnomalySnapshotsAction(payload));

  // Polling refs
  const pollingRef = React.useRef(null);
  const pollingAttemptsRef = React.useRef(0);
  const latestSnapshotRef = React.useRef('');

  // Keep a ref of the latest store value for polling checks
  useEffect(() => {
    latestSnapshotRef.current = snapshotBytesFromStore;
  }, [snapshotBytesFromStore]);

  const selectedAnomalyIsUserAdded = useMemo(
    () => get(selectedAnomaly, 'properties.isUserAdded', false),
    [get(selectedAnomaly, 'properties.isUserAdded')],
  );

  const selectedAnomalyStatus = useMemo(
    () => get(selectedAnomaly, 'properties.status', ''),
    [get(selectedAnomaly, 'properties.status')],
  );

  // Derived values
  const selectedAnomalyIsRemoved = selectedAnomalyStatus === ANOMALY_DECISIONS.REMOVED;
  const selectedAnomalyIsConfirmed = selectedAnomalyStatus === ANOMALY_DECISIONS.CONFIRMED;
  const selectedAnomalyIsConfirmable = CONFIRMABLE_DECISIONS.includes(selectedAnomalyStatus);

  const getAnomalyType = useCallback(() => {
    if (isFacetClickDisplay) {
      return 'workflowpanel.area';
    }
    if (!selectedAnomalyIsUserAdded && !selectedAnomalyIsConfirmed) {
      return 'workflowpanel.anomaly';
    }
    if (selectedAnomalyIsUserAdded || selectedAnomalyIsConfirmed) {
      return 'workflowpanel.damage';
    }
    return '';
  }, [isFacetClickDisplay, selectedAnomalyIsUserAdded, selectedAnomalyIsConfirmed]);

  useEffect(() => {
    if (!isEmpty(snapshotBytesFromStore)
        && snapshotBytesFromStore !== LOADED_ANOMALY_SNAPSHOTS
        && snapshotBytesFromStore !== POLLING_ANOMALY_SNAPSHOTS
    ) {
      setSnapshotData(snapshotBytesFromStore);
      clearAnomalySnapshot({ anomalyId: selectedAnomalyId });
      setRetryingSnapshot(false);
    }
  }, [snapshotBytesFromStore]);

  // Manual polling function to be triggered explicitly
  const pollSnapshotUntilReady = useCallback((anomalyId, maxAttempts = 5, intervalMs = 2000) => {
    // Clear any existing polling
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
    pollingAttemptsRef.current = 0;
    setRetryingSnapshot(true);

    const tick = () => {
      pollingAttemptsRef.current += 1;

      pollingRef.current = setTimeout(() => {
        const { current } = latestSnapshotRef;
        const shouldStop = current !== POLLING_ANOMALY_SNAPSHOTS;

        if (shouldStop) {
          setRetryingSnapshot(false);
          clearTimeout(pollingRef.current);
          pollingRef.current = null;
          return;
        }

        if (pollingAttemptsRef.current >= maxAttempts) {
          // Mark as failed in store (ensure not in POLLING state)
          clearAnomalySnapshot({ anomalyId });
          fetchAnomalySnapshots({ anomalyIds: [anomalyId], orderId });
          setRetryingSnapshot(false);
          clearTimeout(pollingRef.current);
          pollingRef.current = null;
          return;
        }

        // Continue polling
        tick();
      }, intervalMs);
    };

    tick();
  }, [dispatch, fetchAnomalySnapshots, orderId, clearAnomalySnapshot]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (retryingSnapshot) {
      pollSnapshotUntilReady(selectedAnomalyId, 5, 2000);
    }
  }, [retryingSnapshot]);

  const retryScreenshot = () => {
    generateAnomalySnapshots({ anomalyIds: [selectedAnomalyId], orderId });
    setRetryingSnapshot(true);
  };

  let imageContent;
  if (!snapshotData || retryingSnapshot) {
    imageContent = (
      <EVBox display="flex" justifyContent="center" alignItems="center" style={{ height: '100%', minHeight: '200px' }}>
        <EVCircularProgress size={40} />
      </EVBox>
    );
  } else if (snapshotData === 'failed') {
    imageContent = (
      <EVBox display="flex" justifyContent="center" alignItems="center" style={{ height: '100%', minHeight: '200px' }}>
        <EVIconButton
          style={{ color: 'red' }}
          onClick={() => retryScreenshot()}
          aria-label="Retry Screenshot"
        >
          <EVRefreshOutlinedIcon />
        </EVIconButton>
      </EVBox>
    );
  } else {
    imageContent = (
      <>
        <img src={`data:image/jpeg;base64,${snapshotData}`} className={styles.anomalyImage} alt="Anomaly" />
        {isFacetRawImagesEnabled && (
          <EVIconButton id="rawImagesButton" onClick={onExpandClick} className={styles.expansionButton}>
            <EVExpand className={styles.expansionIcon} />
          </EVIconButton>
        )}
      </>
    );
  }

  return (
    <>
      <EVBox
        className={clsx([
          highlight ? styles.subContainerHighlighted : styles.subContainer,
        ])}
        onMouseEnter={() => onAnomalyCardHover(selectedAnomalyId)}
        onMouseDownCapture={() => onAnomalyCardHover(selectedAnomalyId)}
      >
        <EVBox
          className={clsx([styles.heightBar, styles.flexEnd])}
          onMouseEnter={() => onAnomalyCardHover(selectedAnomalyId)}
        >
          <EVTypography className={clsx([styles.remainingSpace], styles.annomalyTypeText)}>
            {t(getAnomalyType())}
          </EVTypography>
          {showAnomalyNotesButton && (
            <EVIconButton
              className={clsx({ [styles.iconButtons]: true },
                { [styles.anomalyNotesButtonSelected]: showAnomalyDescription },
                { [styles.anomalyNotesButtonHighlighted]: isAnomalyDescriptionPresent })}
              onClick={() => onOpenAnomalyDescription(selectedAnomalyId)}
              disabled={selectedAnomalyId === UNSET_ANOMALY_ID || !selectedAnomalyId || loading[GET_ANOMALY_NOTES]}
            >
              <EVNoteIcon />
            </EVIconButton>
          )}
        </EVBox>
        {isEntitled(entitlements, FEATURE_ENTITLEMENTS.MANAGE_DECISIONS) && !orderCompleted && (
          <EVBox
            className={styles.typeBar}
            onMouseEnter={() => onAnomalyCardHover(selectedAnomalyId)}
            onMouseDown={() => onAnomalyCardHover(selectedAnomalyId)}
          >
            <EVTypography className={styles.typeTitle}>
              {t('workflowpanel.damageType')}
            </EVTypography>
            <EVFormControl
              className={styles.selectBar}
              variant="outlined"
              size="small"
              onFocus={() => onAnomalyCardHover(selectedAnomalyId)}
              onMouseDown={() => onAnomalyCardHover(selectedAnomalyId)}
            >
              <EVSelect
                id="anomalyTypeSelect"
                className={styles.select}
                onChange={onTypeChange}
                value={getMappedAnomalyType()}
                onMouseEnter={() => onAnomalyCardHover(selectedAnomalyId)}
                onMouseDown={() => onAnomalyCardHover(selectedAnomalyId)}
                onClick={() => onAnomalyCardHover(selectedAnomalyId)}
                onOpen={() => onAnomalyCardHover(selectedAnomalyId)}
                MenuProps={{
                  PaperProps: {
                    onMouseEnter: () => onAnomalyCardHover(selectedAnomalyId),
                    'data-ev-select-anomaly-id': selectedAnomalyId,
                  },
                  MenuListProps: {
                    onMouseEnter: () => onAnomalyCardHover(selectedAnomalyId),
                    'data-ev-select-anomaly-id': selectedAnomalyId,
                  },
                }}
              >
                {selectedAnomaly && get(selectedAnomaly, 'properties.isUserAdded', false) && (
                  <EVMenuItem
                    className={styles.selectBarItem}
                    value="User Defined Anomaly"
                    onMouseEnter={() => onAnomalyCardHover(selectedAnomalyId)}
                  >
                    {t('workflowpanel.userDefined')}
                  </EVMenuItem>
                )}
                {anomalyTypeOptions.map((option) => (
                  <EVMenuItem
                    className={styles.selectBarItem}
                    value={option.value}
                    key={option.value}
                    onMouseEnter={() => onAnomalyCardHover(selectedAnomalyId)}
                  >
                    {t(option.label)}
                  </EVMenuItem>
                ))}
              </EVSelect>
            </EVFormControl>
          </EVBox>
        )}
        <EVBox
          className={styles.anomalyMapContainer}
          id="workflowMapViewer"
          onMouseEnter={() => onAnomalyCardHover(selectedAnomalyId)}
        >
          <ErrorBoundary
            componentName="WorkflowPanel: MapViewer"
            alertMsg={t('workFlowPanel.mapViewerCrash')}
          >
            {imageContent}
          </ErrorBoundary>
        </EVBox>
        <EVBox
          className={styles.heightBar}
          onMouseEnter={() => onAnomalyCardHover(selectedAnomalyId)}
        >
          {selectedAnomalyId === UNSET_ANOMALY_ID
            && isFacetClickDisplay
            && isEntitled(entitlements, FEATURE_ENTITLEMENTS.MANAGE_DECISIONS)
            && !orderCompleted
            && (
              <EVBox className={styles.markDamage}>
                <EVButton
                  id="confirmAnomalyDecision"
                  className={styles.buttonConfirm}
                  color="primary"
                  variant="contained"
                  disableElevation
                  onClick={() => onMarkDamage(true, selectedAnomalyId)}
                >
                  {t('workflowpanel.markDamage')}
                </EVButton>
              </EVBox>
            )}
          <EVBox display="flex" justifyContent="center" alignItems="center">
            {showIncludeInReport && (
              <>
                <EVPaperClipIcon color="primary" />
                <EVSwitch
                  id="includeAnomalyInReport"
                  color="primary"
                  checked={includeInReportChecked}
                  disabled={includeInReportDisabled}
                  onChange={onIncludeInReport}
                />
              </>
            )}
          </EVBox>
          {selectedAnomalyId !== UNSET_ANOMALY_ID && (
            (isEntitled(entitlements, FEATURE_ENTITLEMENTS.MANAGE_DECISIONS) && !orderCompleted) ? (
              <EVBox>
                {selectedAnomalyIsUserAdded && (
                  <EVButton
                    id="deleteAnomalyUserAdded"
                    className={styles.buttonRemove}
                    variant="outlined"
                    disabled={error[FETCH_ANOMALY_METADATA]}
                    onClick={handleDeleteUserAnomalyOpen}
                  >
                    {t('workflowpanel.remove')}
                  </EVButton>
                )}
                {!selectedAnomalyIsUserAdded && !selectedAnomalyIsRemoved && (
                  <EVButton
                    id="removeAnomalyDecision"
                    className={styles.buttonRemove}
                    variant="outlined"
                    disabled={error[FETCH_ANOMALY_METADATA]}
                    onClick={() => confirmOrRemoveAnomalyDecision(true, selectedAnomalyId)}
                  >
                    {selectedAnomalyIsConfirmed ? t('workflowpanel.remove') : t('workflowpanel.hide')}
                  </EVButton>
                )}
                {!selectedAnomalyIsUserAdded && selectedAnomalyIsRemoved && (
                  <EVButton
                    id="reinstateAnomalyDecision"
                    variant="outlined"
                    className={styles.buttonRemove}
                    disabled={error[FETCH_ANOMALY_METADATA]}
                    onClick={() => confirmOrRemoveAnomalyDecision(false, selectedAnomalyId)}
                  >
                    {t('workflowpanel.reinstate')}
                  </EVButton>
                )}
                {((!selectedAnomalyIsUserAdded && selectedAnomalyIsConfirmable) || isFacetClickDisplay)
                  && (
                    <EVButton
                      id="confirmAnomalyDecision"
                      className={styles.buttonConfirm}
                      color="primary"
                      disabled={error[FETCH_ANOMALY_METADATA]}
                      variant="contained"
                      disableElevation
                      onClick={() => confirmOrRemoveAnomalyDecision(false, selectedAnomalyId)}
                    >
                      {t('workflowpanel.markDamage')}
                    </EVButton>
                  )}
              </EVBox>
            ) : (
              <EVBox>
                <EVTypography>
                  {(selectedAnomalyIsConfirmed && t('workflowPanel.confirmed'))
                    || (selectedAnomalyIsRemoved && t('workflowPanel.removed'))}
                </EVTypography>
              </EVBox>
            )
          )}
        </EVBox>
      </EVBox>
    </>
  );
};

AnomalyDecisionCard.propTypes = {
  t: PropTypes.func.isRequired,
  anomalyTypeOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })).isRequired,
  onTypeChange: PropTypes.func.isRequired,
  showAnomalyDescription: PropTypes.bool.isRequired,
  isAnomalyDescriptionPresent: PropTypes.bool.isRequired,
  onOpenAnomalyDescription: PropTypes.func.isRequired,
  UNSET_ANOMALY_ID: PropTypes.string.isRequired,
  GET_ANOMALY_NOTES: PropTypes.string.isRequired,
  isEntitled: PropTypes.func.isRequired,
  FEATURE_ENTITLEMENTS: PropTypes.objectOf(PropTypes.string).isRequired,
  selectedAnomaly: PropTypes.shape({
    geometries: PropTypes.arrayOf(PropTypes.shape({
      type: PropTypes.string,
      coordinates: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.number), // For Point geometries
        PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number))), // For Polygon geometries
      ]),
    })),
    properties: PropTypes.shape({
      anomalyId: PropTypes.string,
      facetId: PropTypes.string,
      status: PropTypes.string,
      isUserAdded: PropTypes.bool,
      imageUrn: PropTypes.string,
      confidence: PropTypes.string,
      type: PropTypes.string,
      includeInReport: PropTypes.bool,
      annotationType: PropTypes.string,
    }),
    style: PropTypes.shape({
      text: PropTypes.shape({
        backgroundImage: PropTypes.string,
      }),
    }),
  }),
  getMappedAnomalyType: PropTypes.func.isRequired,
  onMarkDamage: PropTypes.func.isRequired,
  isFacetClickDisplay: PropTypes.bool.isRequired,
  FETCH_ANOMALY_METADATA: PropTypes.string.isRequired,
  handleDeleteUserAnomalyOpen: PropTypes.func.isRequired,
  confirmOrRemoveAnomalyDecision: PropTypes.func.isRequired,
  onIncludeInReport: PropTypes.func.isRequired,
  showIncludeInReport: PropTypes.bool.isRequired,
  includeInReportChecked: PropTypes.bool.isRequired,
  includeInReportDisabled: PropTypes.bool.isRequired,
  onExpandClick: PropTypes.func.isRequired,
  highlight: PropTypes.bool,
  onAnomalyCardHover: PropTypes.func,
};

AnomalyDecisionCard.defaultProps = {
  selectedAnomaly: null,
  highlight: false,
  onAnomalyCardHover: () => {},
};

export default AnomalyDecisionCard;
